import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, MessageCircle } from "lucide-react";
import { Banner } from "../../types";
import { useAutoSlider } from "../../hooks/useAutoSlider";
import { useAllCityTranslations } from "../../hooks/useBusinessData";
import { useBannerFonts } from "../../hooks/useBannerFonts";
import { shouldIgnoreClick } from "../../utils/clickGuard";
import { bannerImgAttrs, CTX_SIZES } from "../../utils/bannerImg";
import PendingApprovalOverlay from "../ui/PendingApprovalOverlay";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { Autoplay, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "./bannerSlider.css";

// 사용자 인터랙션 후 자동 슬라이드 재개까지 대기 시간 (ms)
const RESUME_AFTER_MS = 6000;
// swipe 최소 이동 거리 (px)
const SWIPE_THRESHOLD = 40;

/** 자동 슬라이드 일시 정지 hook — 인터랙션 후 N초 동안 paused */
function usePauseAfterInteraction(): { paused: boolean; pause: () => void } {
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<number | null>(null);
  const pause = useCallback(() => {
    setPaused(true);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setPaused(false), RESUME_AFTER_MS);
  }, []);
  useEffect(() => () => { if (timerRef.current) window.clearTimeout(timerRef.current); }, []);
  return { paused, pause };
}

interface BannerSliderProps {
  banners: Banner[];
  height: number;
  intervalMs: number;
  columns: number;
  // 도시/카테고리 필터가 적용 중일 때 카드에 해당 라벨 노출 여부
  showCity?: boolean;
  showCategory?: boolean;
  square?: boolean;
  /** 어드민 설정 등급 비율 — 지정 시 square 보다 우선 ("16/9", "1/1", "4/3" 등) */
  aspectRatio?: string;
  isMobile?: boolean;
  /** 자동 슬라이드 활성화 (기본 true). 요구사항 #254 #6c: 골드 false. */
  autoSlide?: boolean;
  /** 좌우 화살표 표시 (웹 전용). #375-3: Platinum 만 사용 */
  showArrows?: boolean;
  onBannerClick: (advertiserKey: string) => void;
}

function BannerCard({ banner, onClick, aspectRatio, cityNames, showCity = true, showCategory = true, pagiTotal, pagiActive }: { banner: Banner; onClick: () => void; showCity?: boolean; showCategory?: boolean; square?: boolean; aspectRatio?: string; cityNames: Record<string, string>; pagiTotal?: number; pagiActive?: number }) {
  // 요구사항 #254 #6b: 등급별 aspectRatio prop 적용 (골드는 4/3 → object-cover 잘림 의도)
  const ar = aspectRatio || "16/9";
  const { t } = useTranslation();
  // 등급(tier)별 폰트 — useBannerFonts 가 string tier 자동 매핑
  const { titleSize, subtitleSize } = useBannerFonts(banner?.tier);
  if (!banner) return null;
  // 광고주 표시 옵션 + 전역 필터 (사용자가 도시/카테고리 선택 시 중복 라벨 제거)
  // showT: 표시 옵션이 켜져 있어야 1행 자체를 그림 (타이틀 비었으면 "제목 없음" placeholder)
  const showT = !!banner.showTitle;
  const showS = !!banner.showSubtitle && banner.subtitle;
  const showCi = showCity && !!banner.showCity && banner.location;
  const showCat = showCategory && !!banner.categoryKey;
  const showCnt = !!banner.showCommentCount || banner.tier === "vip";  // VIP 는 댓글 수 항상 표시
  const showFav = !!banner.showFavoriteCount;
  const hasLine2 = showS || showCi || showCat || showCnt || showFav;
  return (
    <button
      onClick={(e) => { if (banner.isConfirmed === false || shouldIgnoreClick()) { e.preventDefault(); return; } onClick(); }}
      aria-disabled={banner.isConfirmed === false}
      className={`flex w-full flex-col self-start bg-white text-left ${banner.isConfirmed === false ? "cursor-not-allowed" : ""}`}
    >
      {/* 이미지 영역 — 4면 모두 라운드 + 하단 그라데이션 위 화이트 타이틀 오버레이 */}
      <div
        className={`relative block w-full shrink-0 overflow-hidden bg-slate-100 ${banner.tier === "vip" ? "" : "rounded-md"}`}
        style={{ aspectRatio: ar }}
      >
        <img
          {...bannerImgAttrs(banner.imageUrl, banner.imageSizes, CTX_SIZES.slider)}
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        {!banner.isConfirmed && <PendingApprovalOverlay />}
        {/* 타이틀 오버레이 — 이미지 하단부 위에 그라데이션 + 화이트 텍스트.
            VIP 는 하단 페이지네이션 바와 겹치지 않게 pb 확보(#375-1) */}
        {showT && banner.title && (
          /* #438-2: VIP 페이지네이션 삭제로 하단 회피 여백 불필요 → pb-1 통일 */
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 pt-6 pb-1 z-10">
            <div
              className="font-bold text-white text-center truncate leading-tight"
              style={{ fontSize: titleSize }}
            >
              {banner.title}
            </div>
          </div>
        )}
        {/* #375-2: VIP 페이지네이션 — 그레이 pill (하단 중앙). 개수 ≤25 점(많으면 점/간격 축소), >25 현재/전체 텍스트 */}
        {pagiTotal && pagiTotal > 1 && (
          <div className={`absolute bottom-0 left-0 right-0 z-20 flex items-center justify-center bg-slate-800/45 px-2.5 py-1.5 ${pagiTotal > 12 ? "gap-1" : "gap-1.5"}`}>
            {pagiTotal <= 25 ? (
              Array.from({ length: pagiTotal }).map((_, i) => (
                <span
                  key={i}
                  // 선택된 점은 100% 블루로 또렷, 나머지는 50% 투명
                  className={`h-1.5 rounded-full transition-colors ${pagiTotal > 12 ? "w-2" : "w-3"} ${i === pagiActive ? "bg-blue-500" : "bg-white/50"}`}
                />
              ))
            ) : (
              <span className="text-[11px] font-bold leading-none tabular-nums text-white/50">
                <span className="text-blue-500">{(pagiActive ?? 0) + 1}</span> / {pagiTotal}
              </span>
            )}
          </div>
        )}
        {/* 이벤트 리본 */}
        {banner.tier === "event" && (
          <div className="absolute -right-[22px] top-[8px] z-20 rotate-45 bg-red-500 px-6 py-[1px] text-[6px] md:text-[8px] font-black tracking-widest text-white shadow-md">
            {t("banner.event_badge")}
          </div>
        )}
      </div>
      {/* 정보 영역 — 메타(2행) 만. 타이틀은 이미지 위 오버레이로 이동 */}
      {hasLine2 && (
        <div className="px-1.5 pt-0.5 pb-1 shrink-0 bg-white">
          <div
            className="flex items-center gap-1 min-w-0 leading-tight min-h-[16px] text-black"
            style={{ fontSize: subtitleSize }}
          >
            {showCi && (
              <span className="shrink-0 text-black">{cityNames[banner.location] || banner.location}</span>
            )}
            {showS && (
              <span className="flex-1 min-w-0 truncate">{banner.subtitle}</span>
            )}
            {showCat && (
              <span className="shrink-0 text-black">{banner.categoryKey}</span>
            )}
            {showCnt && (
              /* 빈 말풍선 + 풍선 중앙에 카운트 ("..." 자리에 숫자) */
              <span className="relative shrink-0 inline-flex items-center justify-center text-rose-600">
                <MessageCircle className="h-5 w-5" strokeWidth={2} />
                <span className="absolute inset-0 flex items-center justify-center pb-[2px] text-[9px] font-extrabold leading-none tabular-nums">
                  {banner.commentCount && banner.commentCount > 99 ? "99+" : (banner.commentCount || 0)}
                </span>
              </span>
            )}
            {showFav && (
              <span className="shrink-0 text-rose-600 font-bold tabular-nums">❤️ {(banner.favoriteCount || 0).toLocaleString()}</span>
            )}
          </div>
        </div>
      )}
    </button>
  );
}

// VIP (columns=1) — 크로스페이드(원본 복원, 요구사항 #421 원복).
// 배너들을 겹쳐 쌓고(첫 배너=높이 앵커, 나머지 absolute inset-0) active 만 opacity 1 로 페이드.
// opacity 트랜지션이라 Safari/Chrome 모두 정상(clip/mask 재페인트 버그 없음).
// 자동 전환 + 스와이프(한 개씩) + 터치 시 자동 멈춤 + 페이지네이션.
function CrossfadeSlider({ banners, intervalMs, showCity, showCategory, square, showArrows = false, onBannerClick }: Omit<BannerSliderProps, "columns">) {
  const cityNames = useAllCityTranslations();
  const ar = "16/9";
  const n = banners.length;
  const fadeMs = Math.min(intervalMs / 2, 1500);
  const [active, setActive] = useState(0);
  // 요구사항 #522: z-index/클릭판정을 active 변경 즉시 넘기면, opacity 크로스페이드가 아직
  // 진행 중(최대 fadeMs)인데도 화면에 여전히 진하게 보이는 이전 배너가 아니라 이제 막
  // 나타나기 시작한 다음 배너가 클릭을 가로채는 버그가 있었음(사용자가 보는 것과 실제
  // 클릭되는 배너가 어긋남). z-index/pointer-events 전용 상태(visuallyActive)를 따로 두고,
  // opacity 트랜지션이 실제로 끝나는 시점(fadeMs 후)에만 넘겨서 "항상 시각적으로 우세한(마지막
  // 전환이 완료된) 배너만 클릭 가능"하도록 함 — z-index 스위칭 타이밍을 opacity와 일치시키는
  // 근본 수정. active 자체는 opacity 소스로 그대로 즉시 갱신(페이드 시작 트리거는 여전히 즉시).
  const [visuallyActive, setVisuallyActive] = useState(0);
  const [fadeOn, setFadeOn] = useState(false); // 첫 렌더/카테고리 전환 직후엔 트랜지션 off (점프 방지)
  const prevBannersRef = useRef(banners);
  const pausedRef = useRef(false);
  const resumeTimer = useRef<number | null>(null);
  const touchX = useRef<number | null>(null);

  // 첫 렌더 / banners(카테고리) 변경 시 active·트랜지션 리셋
  useEffect(() => {
    if (prevBannersRef.current !== banners) {
      prevBannersRef.current = banners;
      setActive((a) => (a < n ? a : 0));
    }
    setFadeOn(false);
    const t = window.setTimeout(() => setFadeOn(true), 50);
    return () => window.clearTimeout(t);
  }, [banners, n]);

  // active 변경 시 z-index/클릭판정(visuallyActive)은 opacity 트랜지션이 끝난 뒤에만 따라감.
  // fadeOn=false(트랜지션 꺼진 상태 — 첫 렌더/카테고리 전환 직후)면 opacity 자체도 즉시
  // 바뀌므로 visuallyActive도 즉시 맞춤. 연속 전환(빠른 스와이프 등) 시 이전 타이머는 취소.
  useEffect(() => {
    if (!fadeOn) { setVisuallyActive(active); return; }
    const t = window.setTimeout(() => setVisuallyActive(active), fadeMs);
    return () => window.clearTimeout(t);
  }, [active, fadeOn, fadeMs]);

  const pause = useCallback(() => {
    pausedRef.current = true;
    if (resumeTimer.current) window.clearTimeout(resumeTimer.current);
    resumeTimer.current = window.setTimeout(() => { pausedRef.current = false; }, RESUME_AFTER_MS);
  }, []);

  // VIP 좌우 화살표 (플레티넘과 동일 — 웹 md+ 전용, hidden md:flex 로 CSS 차단)
  const goPrev = useCallback(() => { pause(); setActive((a) => (a - 1 + n) % n); }, [n, pause]);
  const goNext = useCallback(() => { pause(); setActive((a) => (a + 1) % n); }, [n, pause]);

  // 자동 크로스페이드
  useEffect(() => {
    if (n <= 1) return;
    const id = window.setInterval(() => {
      if (pausedRef.current) return;
      setActive((a) => (a + 1) % n);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [n, intervalMs]);

  useEffect(() => () => { if (resumeTimer.current) window.clearTimeout(resumeTimer.current); }, []);

  if (n === 0) return null;

  return (
    <div
      className="relative w-full"
      style={{ padding: "0 7.5px" }}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; pause(); }}
      onTouchEnd={(e) => {
        if (touchX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        touchX.current = null;
        if (Math.abs(dx) > SWIPE_THRESHOLD && n > 1) {
          pause();
          setActive((a) => (a + (dx < 0 ? 1 : -1) + n) % n);
        }
      }}
    >
      {banners.map((banner, i) => {
        // 첫 배너(앵커)는 흐름에 두어 카드 자연 높이가 부모 높이를 결정, 나머지는 absolute 오버레이 + 크로스페이드
        const isAnchor = i === 0;
        return (
          <div
            key={banner.advertiserKey}
            className={isAnchor ? "relative" : "absolute inset-0"}
            style={{
              // 앵커(relative)는 부모 content-box(패딩 7.5px 안쪽)에 위치. non-anchor(absolute inset-0)는
              // 패딩박스를 채워 전폭이 되므로, 자체 padding 0 7.5px 로 카드를 동일하게 안쪽으로 밀어 정렬.
              padding: isAnchor ? undefined : "0 7.5px",
              opacity: active === i ? 1 : 0,
              transition: fadeOn ? `opacity ${fadeMs}ms ease-in-out` : "none",
              // #522: z-index/클릭 가능 여부는 opacity 전환이 끝난 뒤(visuallyActive)에만 넘어감 —
              // 전환 중엔 화면에 실제로 우세하게 보이는 배너가 계속 클릭 대상.
              zIndex: visuallyActive === i ? 10 : 0,
              pointerEvents: visuallyActive === i ? "auto" : "none",
            }}
          >
            {/* #438-2: 페이지네이션 동그라미 삭제 → pagiTotal/pagiActive 미전달 */}
            <BannerCard banner={banner} onClick={() => onBannerClick(banner.advertiserKey)} showCity={showCity} showCategory={showCategory} square={square} aspectRatio={ar} cityNames={cityNames} />
          </div>
        );
      })}
      {showArrows && n > 1 && (
        <>
          <button onClick={goPrev} aria-label="prev" className="hidden md:flex absolute left-1 top-1/2 -translate-y-1/2 z-20 h-9 w-9 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm hover:bg-black/60 transition-colors">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button onClick={goNext} aria-label="next" className="hidden md:flex absolute right-1 top-1/2 -translate-y-1/2 z-20 h-9 w-9 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm hover:bg-black/60 transition-colors">
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}
    </div>
  );
}

// 좌측 슬라이드 (columns>=2 전용)
function SlideSlider({ banners, intervalMs, columns, showCity, showCategory, square, aspectRatio, autoSlide = true, showArrows = false, onBannerClick }: BannerSliderProps) {
  // Swiper 기반: 터치 없을 때 자동 좌측 carousel, 터치 시 자동 중지 + 자유 좌우 드래그 (요구사항)
  const cityNames = useAllCityTranslations();
  const totalSlides = banners.length;
  const ar = aspectRatio || "16/9";
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const arrowsOn = showArrows && totalSlides > Math.ceil(columns);
  if (totalSlides === 0) return null;
  // 모바일 (mobile breakpoint width) 에서 columns 가 소수이면 그대로 (예: 2.5)
  return (
    <div className="relative w-full">
      <Swiper
        className={arrowsOn ? "plat-swiper" : undefined}
        modules={arrowsOn ? [Autoplay, Navigation] : [Autoplay]}
        slidesPerView={columns}
        loop={totalSlides > Math.ceil(columns)}
        speed={500}
        autoplay={autoSlide && totalSlides > Math.ceil(columns)
          ? { delay: intervalMs, disableOnInteraction: true }
          : false}
        navigation={arrowsOn ? { prevEl: prevRef.current, nextEl: nextRef.current } : false}
        onBeforeInit={arrowsOn ? (swiper: SwiperType) => {
          const nav = swiper.params.navigation;
          if (nav && typeof nav !== "boolean") {
            nav.prevEl = prevRef.current;
            nav.nextEl = nextRef.current;
          }
        } : undefined}
        grabCursor
      >
        {banners.map((banner) => (
          <SwiperSlide key={banner.advertiserKey} className="!h-auto px-[3.75px] sm:px-[7.5px]">
            <BannerCard banner={banner} onClick={() => onBannerClick(banner.advertiserKey)} showCity={showCity} showCategory={showCategory} square={square} aspectRatio={ar} cityNames={cityNames} />
          </SwiperSlide>
        ))}
      </Swiper>
      {/* #375-3: Platinum 좌우 화살표 — 웹(md+) 전용 */}
      {arrowsOn && (
        <>
          <button ref={prevRef} aria-label="prev" className="hidden md:flex absolute left-1 top-1/2 -translate-y-1/2 z-20 h-9 w-9 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm hover:bg-black/60 transition-colors">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button ref={nextRef} aria-label="next" className="hidden md:flex absolute right-1 top-1/2 -translate-y-1/2 z-20 h-9 w-9 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm hover:bg-black/60 transition-colors">
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}
    </div>
  );
}

// 자유 드래그 슬라이더 — CSS scroll-snap (요구사항 #254 #6d: 손가락/마우스 따라 실시간 이동)
// 골드 광고처럼 자동 슬라이드 OFF + 사용자 드래그 전용.
// #443: showArrows 시 웹(md+) 전용 좌우 화살표 — 자유 드래그는 유지, 클릭 시 한 화면 스크롤.
function ScrollSnapSlider({ banners, columns, showCity, showCategory, square, aspectRatio, showArrows = false, onBannerClick }: BannerSliderProps) {
  const cityNames = useAllCityTranslations();
  const cardWidthPercent = 100 / columns;
  const ar = aspectRatio || "16/9";
  const scrollRef = useRef<HTMLDivElement>(null);
  const arrowsOn = showArrows && banners.length > Math.ceil(columns);
  const scrollByPage = (dir: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };
  return (
    <div className="relative w-full">
      <div
        ref={scrollRef}
        className="flex overflow-x-auto snap-x snap-mandatory [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {banners.map((banner) => (
          <div
            key={banner.advertiserKey}
            className="snap-start shrink-0 px-[3.75px] sm:px-[7.5px]"
            style={{ width: `${cardWidthPercent}%` }}
          >
            <BannerCard banner={banner} onClick={() => onBannerClick(banner.advertiserKey)} showCity={showCity} showCategory={showCategory} square={square} aspectRatio={ar} cityNames={cityNames} />
          </div>
        ))}
      </div>
      {/* #443: 플래티넘과 동일 스타일 좌우 화살표 — 웹(md+) 전용 */}
      {arrowsOn && (
        <>
          <button onClick={() => scrollByPage(-1)} aria-label="prev" className="hidden md:flex absolute left-1 top-1/2 -translate-y-1/2 z-20 h-9 w-9 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm hover:bg-black/60 transition-colors">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button onClick={() => scrollByPage(1)} aria-label="next" className="hidden md:flex absolute right-1 top-1/2 -translate-y-1/2 z-20 h-9 w-9 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm hover:bg-black/60 transition-colors">
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}
    </div>
  );
}

export default function BannerSlider(props: BannerSliderProps) {
  if (props.banners.length === 0) return null;

  if (props.columns === 1) {
    return <CrossfadeSlider {...props} />;
  }

  // 자동 슬라이드 OFF → 자유 드래그(scroll-snap) 모드 (요구사항 #254 #6d)
  if (props.autoSlide === false) {
    return <ScrollSnapSlider {...props} />;
  }

  return <SlideSlider {...props} />;
}

